import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiCalendar, FiClock, FiUser, FiPhone, FiMail, FiCheck } = FiIcons;

const Booking = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    service: '',
    date: '',
    time: '',
    name: '',
    phone: '',
    email: '',
    notes: ''
  });

  const services = [
    {
      id: 'eye-test',
      name: 'Comprehensive Eye Test',
      duration: '45 minutes',
      price: 'Rs. 2,500',
      description: 'Complete vision screening including refraction and eye health check'
    },
    {
      id: 'contact-fitting',
      name: 'Contact Lens Fitting',
      duration: '60 minutes',
      price: 'Rs. 3,500',
      description: 'Professional contact lens fitting and training session'
    },
    {
      id: 'frame-consultation',
      name: 'Frame Style Consultation',
      duration: '30 minutes',
      price: 'Free',
      description: 'Expert guidance in choosing the perfect frames for your face'
    },
    {
      id: 'repair-service',
      name: 'Spectacle Repair',
      duration: '15 minutes',
      price: 'Rs. 500+',
      description: 'Quick repairs and adjustments for your existing glasses'
    }
  ];

  const timeSlots = [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM'
  ];

  const handleNext = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Booking submitted:', formData);
    setCurrentStep(5); // Show confirmation
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen pt-16 pb-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Link to="/" className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 mb-6">
            <SafeIcon icon={FiArrowLeft} />
            <span>Back to Home</span>
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Book Your <span className="gradient-text">Appointment</span>
          </h1>
          <p className="text-xl text-white/80">
            Schedule your visit in just a few simple steps
          </p>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6 mb-8"
        >
          <div className="flex justify-between items-center mb-4">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                  currentStep >= step
                    ? 'bg-blue-500 border-blue-500 text-white'
                    : 'border-white/30 text-white/50'
                }`}
              >
                {currentStep > step ? <SafeIcon icon={FiCheck} /> : step}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-sm text-white/70">
            <span>Service</span>
            <span>Date & Time</span>
            <span>Details</span>
            <span>Confirm</span>
          </div>
        </motion.div>

        {/* Form Steps */}
        <div className="glass-card p-8">
          {/* Step 1: Service Selection */}
          {currentStep === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
            >
              <h2 className="text-2xl font-bold text-white mb-6">Select Service</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {services.map((service) => (
                  <motion.div
                    key={service.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => updateFormData('service', service.id)}
                    className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.service === service.id
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-white/20 hover:border-white/40'
                    }`}
                  >
                    <h3 className="text-xl font-bold text-white mb-2">{service.name}</h3>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-blue-400 font-semibold">{service.price}</span>
                      <span className="text-white/70 text-sm">{service.duration}</span>
                    </div>
                    <p className="text-white/70 text-sm">{service.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Date & Time Selection */}
          {currentStep === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
            >
              <h2 className="text-2xl font-bold text-white mb-6">Select Date & Time</h2>
              
              <div className="mb-8">
                <label className="block text-white font-medium mb-3">
                  <SafeIcon icon={FiCalendar} className="inline mr-2" />
                  Choose Date
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => updateFormData('date', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="glass-input w-full p-3 text-white"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-3">
                  <SafeIcon icon={FiClock} className="inline mr-2" />
                  Available Time Slots
                </label>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  {timeSlots.map((time) => (
                    <motion.button
                      key={time}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => updateFormData('time', time)}
                      className={`p-3 rounded-lg font-medium transition-all ${
                        formData.time === time
                          ? 'glass-button-primary text-white'
                          : 'glass-button text-white/70 hover:text-white'
                      }`}
                    >
                      {time}
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Personal Details */}
          {currentStep === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
            >
              <h2 className="text-2xl font-bold text-white mb-6">Your Details</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-white font-medium mb-2">
                    <SafeIcon icon={FiUser} className="inline mr-2" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => updateFormData('name', e.target.value)}
                    className="glass-input w-full p-3 text-white placeholder-white/50"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    <SafeIcon icon={FiPhone} className="inline mr-2" />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateFormData('phone', e.target.value)}
                    className="glass-input w-full p-3 text-white placeholder-white/50"
                    placeholder="07X XXX XXXX"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    <SafeIcon icon={FiMail} className="inline mr-2" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    className="glass-input w-full p-3 text-white placeholder-white/50"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => updateFormData('notes', e.target.value)}
                    className="glass-input w-full p-3 text-white placeholder-white/50 resize-none"
                    rows="3"
                    placeholder="Any specific requirements or concerns..."
                  ></textarea>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Confirmation */}
          {currentStep === 4 && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
            >
              <h2 className="text-2xl font-bold text-white mb-6">Confirm Appointment</h2>
              <div className="space-y-4 mb-8">
                <div className="glass-card p-4">
                  <h3 className="font-semibold text-white mb-2">Service</h3>
                  <p className="text-white/70">
                    {services.find(s => s.id === formData.service)?.name}
                  </p>
                </div>
                <div className="glass-card p-4">
                  <h3 className="font-semibold text-white mb-2">Date & Time</h3>
                  <p className="text-white/70">{formData.date} at {formData.time}</p>
                </div>
                <div className="glass-card p-4">
                  <h3 className="font-semibold text-white mb-2">Contact</h3>
                  <p className="text-white/70">{formData.name}</p>
                  <p className="text-white/70">{formData.phone}</p>
                  {formData.email && <p className="text-white/70">{formData.email}</p>}
                </div>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
                <p className="text-blue-300 text-sm">
                  <SafeIcon icon={FiCheck} className="inline mr-2" />
                  You will receive an SMS confirmation shortly after booking
                </p>
              </div>
            </motion.div>
          )}

          {/* Step 5: Success */}
          {currentStep === 5 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-12"
            >
              <div className="glass-icon mx-auto mb-6 w-20 h-20">
                <SafeIcon icon={FiCheck} className="text-4xl text-green-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">
                Appointment Confirmed!
              </h2>
              <p className="text-white/70 mb-8">
                We've sent a confirmation SMS to {formData.phone}
              </p>
              <Link to="/">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="glass-button-primary px-8 py-4 text-white font-medium"
                >
                  Back to Home
                </motion.button>
              </Link>
            </motion.div>
          )}

          {/* Navigation Buttons */}
          {currentStep < 5 && (
            <div className="flex justify-between mt-12">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePrev}
                disabled={currentStep === 1}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  currentStep === 1
                    ? 'opacity-50 cursor-not-allowed glass-button'
                    : 'glass-button text-white hover:text-white'
                }`}
              >
                Previous
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={currentStep === 4 ? handleSubmit : handleNext}
                disabled={
                  (currentStep === 1 && !formData.service) ||
                  (currentStep === 2 && (!formData.date || !formData.time)) ||
                  (currentStep === 3 && (!formData.name || !formData.phone))
                }
                className="glass-button-primary px-6 py-3 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentStep === 4 ? 'Confirm Booking' : 'Next'}
              </motion.button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Booking;